package com.example.mystybag.core.extensions

inline fun <T, R> List<T>.mapToArray(transform: (T) -> R): ArrayList<R> {
    return this.mapTo(arrayListOf(), transform)
}

inline fun <T, R> List<T>.mapIndexedToArray(transform: (Int, T) -> R): ArrayList<R> {
    return this.mapIndexedTo(arrayListOf(), transform)
}

inline fun <T> List<T>.filterToArray(predicate: (T) -> Boolean): ArrayList<T> {
    return this.filterTo(arrayListOf(), predicate)
}

inline fun <T> List<T>.filterBySearchIds(
    searchedIds: Set<Long>,
    idOf: (T) -> Long
): List<T> {
    if (searchedIds.isEmpty()) return this
    return filter { idOf(it) in searchedIds }
}

inline fun <T, R> Iterable<T>.mapDistinct(predicate: (T) -> R): List<R> {
    return this.mapTo(mutableSetOf(), predicate).toList()
}

fun ByteArray?.isNullOrEmpty(): Boolean {
    return (this == null) || this.isEmpty()
}

/**
 * Adds an element to the end of this list and returns the list itself
 *
 * @param element The element to add
 * @return The modified list
 */
fun <E> MutableList<E>.insertLast(element: E): MutableList<E> {
    add(element)
    return this
}

/**
 * Adds an element to the beginning of the list and returns the list itself
 *
 * @param element The element to add
 * @return The modified list
 */
fun <E> MutableList<E>.insertFirst(element: E): MutableList<E> {
    add(0, element)
    return this
}

/**
 * Removes an element at the specified position in the list and returns the list itself
 *
 * @param index The index to be removed
 * @return The modified list
 */
fun <E> MutableList<E>.deleteAt(index: Int): MutableList<E> {
    removeAt(index)
    return this
}

/**
 * Removes the first element from this mutable list and returns that removed element, or throws [NoSuchElementException] if this list is empty.
 * **This function is a drop-in replacement of [MutableList.removeFirst] as its name clashes with the new SequencedCollection in Android 15.**
 *
 * @return The removed element
 */
fun <E> MutableList<E>.deleteFirst(): E = if (isEmpty()) throw NoSuchElementException("List is empty.") else removeAt(0)

/**
 * Removes the last element from this mutable list and returns that removed element, or throws [NoSuchElementException] if this list is empty.
 * **This function is a drop-in replacement of [MutableList.removeLast] as its name clashes with the new SequencedCollection in Android 15.**
 *
 * @return The removed element
 */
fun <E> MutableList<E>.deleteLast(): E = if (isEmpty()) throw NoSuchElementException("List is empty.") else removeAt(lastIndex)